import { Chapter, Module, PossibleAnswer, Question } from "types/type.module";
import {
  GuidanceOrExample,
  Link,
  LinkId,
  Reference,
  ReferenceId,
  Term,
  TermId,
  UserProgress,
} from "types/types";

const getToken = () => window.userToken || localStorage.getItem("token");

async function get(url: string, isUpdated?: boolean) {
  return fetch(
    `${process.env.REACT_APP_ENDPOINT}${
      process.env.REACT_APP_ENDPOINT_VERSION
    }${url}${isUpdated ? "?t=" + Date.now() : ""}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        oid: window.localStorage.getItem("oid") || "",
        "portal": window.localStorage.getItem("portal") || ""
      },
    }
  );
}

async function post(url: string, body: BodyInit) {
  return fetch(
    `${process.env.REACT_APP_ENDPOINT}${process.env.REACT_APP_ENDPOINT_VERSION}${url}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        oid: window.localStorage.getItem("oid") || "",
        "portal": window.localStorage.getItem("portal") || ""
      },
      body: body,
    }
  );
}

async function patch(url: string, body: BodyInit) {
  return fetch(
    `${process.env.REACT_APP_ENDPOINT}${process.env.REACT_APP_ENDPOINT_VERSION}${url}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        oid: window.localStorage.getItem("oid") || "",
        "portal": window.localStorage.getItem("portal") || ""
      },
      body,
    }
  );
}

async function deleteCall(url: string) {
  return fetch(
    `${process.env.REACT_APP_ENDPOINT}${process.env.REACT_APP_ENDPOINT_VERSION}${url}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        oid: window.localStorage.getItem("oid") || "",
        "portal": window.localStorage.getItem("portal") || ""
      },
    }
  );
}

export async function initializeUser({
  invitationCode,
}: {
  invitationCode?: string | null;
}) {
  const baseUrl = "/user/profile";
  const url = `${baseUrl}${
    invitationCode !== undefined && invitationCode !== null
      ? `?invitation_code=${invitationCode}`
      : ""
  }`;

  return get(url)
    .then((r) => r.json())
    .then((j) => j.data);
}

/**
   @function postModule
   add module to backend
   @param orgId {string} - organization id
   @param name {string} - name or module
   @param isLive {boolean} - module is live
   @param isFree {boolean} - module is free
   @param displayOrder {number} - order of module
   @param nextModuleId {string} - next module id
*/

type PayloadModule = {
  orgId: string;
  name: string;
  isLive: boolean;
  isFree: boolean;
  displayOrder?: number;
  nextModuleId?: string;
};

export async function postModule({
  orgId,
  name,
  isLive,
  isFree,
  displayOrder,
  nextModuleId,
}: PayloadModule) {
  const body = JSON.stringify({
    name: name,
    is_live: isLive,
    is_free: isFree,
    display_order: displayOrder,
    next_module_id: nextModuleId,
  });

  return post(`/organisations/${orgId}/modules`, body).then((r) =>
    r.json().then((r: { data: Module }) => r.data)
  );
}

/**
   @function postTerm
   post term 
   @param orgId {string} - organization id
   @param name {string} - name of term
   @param description {object} - description of term
*/

type PayloadTerm = {
  orgId: string;
  name: string;
  description: Record<string, any>;
};

export async function postTerm({ orgId, name, description }: PayloadTerm) {
  const body = JSON.stringify({
    name: name,
    description: stringifyIfObject(description),
  });

  return post(`/organisations/${orgId}/terms`, body).then((r) => r.json());
}

/**
   @function deleteTerm
   delete term 
   @param orgId {string} - organization id
   @param id {string} - id
*/

type PayloadDeleteTerm = {
  orgId: string;
  id: TermId;
};

export async function deleteTerm({ orgId, id }: PayloadDeleteTerm) {
  return deleteCall(`/organisations/${orgId}/terms/${id}`);
}

/**
   @function patchTerm
   patch term 
   @param orgId {string} - organization id
   @param name {string} - name of term
   @param description {object} - description of term
   @param id {string} - id of term
*/

type PayloadPatchTerm = {
  orgId: string;
  name: string;
  description: Record<string, any>;
  id: TermId;
};

export async function patchTerm({
  orgId,
  name,
  description,
  id,
}: PayloadPatchTerm) {
  const body = JSON.stringify({
    name: name,
    description: stringifyIfObject(description),
  });

  return patch(`/organisations/${orgId}/terms/${id}`, body).then((r) =>
    r.json()
  );
}

/**
   @function postLink
   post link
   @param orgId {string} - organization id
   @param name {string} - name of link
   @param description {object} - description of link
   @param address {string} - address of link
*/
type PayloadPostLink = {
  orgId: string;
  name: string;
  description: Record<string, any>;
  address: string;
};

export async function postLink({
  orgId,
  name,
  description,
  address,
}: PayloadPostLink): Promise<{ data: Link }> {
  const body = JSON.stringify({
    name: name,
    address: address,
    description: stringifyIfObject(description),
  });

  return post(`/organisations/${orgId}/links`, body).then((r) => r.json());
}

/**
   @function deleteLink
   delete link
   @param orgId {string} - organization id
   @param id {string} - id
*/

type PayloadDeleteLink = {
  orgId: string;
  id: LinkId;
};

export async function deleteLink({ orgId, id }: PayloadDeleteLink) {
  return deleteCall(`/organisations/${orgId}/links/${id}`);
}

/**
   @function strigifyIfObject
   stringify if object
   @param element {object/string} - to stringify
*/
function stringifyIfObject(element: Record<string, any> | string) {
  return typeof element !== "string" ? JSON.stringify(element) : element;
}

/**
   @function patchLink
   patch link
   @param orgId {string} - organization id
   @param name {string} - name of link
   @param description {object} - description of link
   @param address {string} - address of link
   @param id {string} - id of term
*/

type PayloadPatchLink = {
  orgId: string;
  name: string;
  description: Record<string, any>;
  address: string;
  id: LinkId;
};

export async function patchLink({
  orgId,
  name,
  description,
  address,
  id,
}: PayloadPatchLink) {
  const body = JSON.stringify({
    name: name,
    description: stringifyIfObject(description),
    address: address,
  });

  return patch(`/organisations/${orgId}/links/${id}`, body).then((r) =>
    r.json()
  );
}

/**
   @function postRef
   post ref
   @param orgId {string} - organization id
   @param code {string} - code of ref
   @param description {object} - description of ref
   @param link {string} - link of ref
*/

type PayloadPostRef = {
  orgId: string;
  code: string;
  description: Record<string, any>;
  link: string;
};

export async function postRef({
  orgId,
  code,
  description,
  link,
}: PayloadPostRef): Promise<{ data: Reference }> {
  const body = JSON.stringify({
    code: code,
    link: link,
    description: JSON.stringify(description),
  });

  return post(`/organisations/${orgId}/references`, body).then((r) => r.json());
}

/**
   @function deleteReference
   delete reference
   @param orgId {string} - organization id
   @param id {string} - id
*/

type PayloadDeleteReference = {
  orgId: string;
  id: ReferenceId;
};

export async function deleteReference({ orgId, id }: PayloadDeleteReference) {
  return deleteCall(`/organisations/${orgId}/references/${id}`);
}

/**
   @function patchRef
   patch ref
   @param orgId {string} - organization id
   @param code {string} - code of ref
   @param description {object} - description of ref
   @param link {string} - link of ref
   @param id {string} - id of term
*/

type PayloadPatchRef = {
  orgId: string;
  code: string;
  description: Record<string, any>;
  link: string;
  id: ReferenceId;
};

export async function patchRef({
  orgId,
  code,
  description,
  link,
  id,
}: PayloadPatchRef) {
  const body = JSON.stringify({
    code: code,
    description: stringifyIfObject(description),
    link: link,
  });

  return patch(`/organisations/${orgId}/references/${id}`, body).then((r) =>
    r.json()
  );
}

type PayloadPostUserProgress = {
  userId: string;
  moduleId: string;
  name: string;
  data: Record<string, any>;
};

export async function postUserProgress({
  userId,
  moduleId,
  name,
  data,
}: PayloadPostUserProgress) {
  const body = JSON.stringify({
    user_id: userId,
    module_id: moduleId,
    name: name,
    data: JSON.stringify(data),
  });

  return post("/user/progress", body)
    .then((r) => r.json())
    .then((r: { data: UserProgress }) => r.data);
}

type PayloadSendEmail = {
  email: string;
  content: string;
  custom?: {
    module_name: string;
    module_id: string;
  };
};

export async function postSendEmail({
  email,
  content,
  custom,
}: PayloadSendEmail) {
  const body = JSON.stringify({
    receiver_email_address: email,
    subject: "module conclusion",
    email_content: content,
    custom,
  });

  return post("/user/sendemail", body).then((r) => r.json());
}

type PayloadPostChapter = {
  orgId: string;
  moduleId: string;
  name: string;
  orderNumber: number;
};

export async function postChapter({
  orgId,
  moduleId,
  name,
  orderNumber,
}: PayloadPostChapter) {
  const body = JSON.stringify({
    module_id: moduleId,
    name,
    order_number: orderNumber,
  });

  return post(`/organisations/${orgId}/chapters`, body)
    .then((r) => r.json())
    .then((r: { data: Chapter }) => r.data);
}

export type PayloadPostPossibleAnswer = Pick<
  PossibleAnswer,
  "order_number" | "content" | "single" | "question_id" | "comment"
> & {
  orgId: string;
};

export async function postPossibleAnswers({
  orgId,
  order_number,
  content,
  single,
  question_id,
  comment,
}: PayloadPostPossibleAnswer) {
  const body = JSON.stringify({
    question_id,
    content: typeof content !== "string" ? JSON.stringify(content) : content,
    order_number,
    single: single,
    comment: comment,
  });

  return post(
    `/organisations/${orgId}/possible_answers`,

    body
  ).then((r) => r.json());
}
export type PayloadPostQuestion = Pick<
  Question,
  | "chapter_id"
  | "content"
  | "order_number"
  | "conclusion"
  | "display_index"
  | "multiple"
  | "should_be_hidden"
  | "trigger"
  | "comment"
  | "print"
> & {
  orgId: string;
};

export async function postQuestion({
  orgId,
  chapter_id,
  content,
  order_number,
  conclusion,
  display_index,
  multiple,
  print,
  should_be_hidden,
  trigger,
  comment,
}: PayloadPostQuestion) {
  const body = JSON.stringify({
    chapter_id: chapter_id,
    conclusion: conclusion,
    content: typeof content !== "string" ? JSON.stringify(content) : content,
    display_index,
    multiple: multiple,
    order_number: order_number,
    print: print,
    should_be_hidden,
    trigger: JSON.stringify(trigger),
    comment: comment,
  });

  return post(`/organisations/${orgId}/questions`, body).then((r) => r.json());
}

/**
   @function postGuidance
   patch guidance 
   @param orgId {string} - organization id
   @param order_number {number} - order_number of chapter
   @param content {object} - content of possible answer
   @param questionId {string} - questionId 
   @param possibleAnswerId {string} - id for possible answer
   @param guidanceId {string} - id for guidance
*/

type PayloadPostGuidance = Pick<
  GuidanceOrExample,
  "order_number" | "content"
> & {
  orgId: string;
  questionId: string | null;
  possibleAnswerId: string | null;
};

export async function postGuidance({
  orgId,
  order_number,
  content,
  questionId,
  possibleAnswerId,
}: PayloadPostGuidance) {
  const body = JSON.stringify({
    ...(questionId && { question_id: questionId }),
    ...(possibleAnswerId && { possible_answer_id: possibleAnswerId }),
    content: JSON.stringify(content),
    order_number: order_number,
  });

  return post(`/organisations/${orgId}/guidances/`, body).then((r) => r.json());
}

/**
   @function patchExample
   patch guidance 
   @param orgId {string} - organization id
   @param order_number {number} - order_number of chapter
   @param content {object} - content of possible answer
   @param questionId {string} - questionId 
   @param possibleAnswerId {string} - id for possible answer
   @param exampleId {string} - id for guidance
*/

type PayloadPostExample = Pick<
  GuidanceOrExample,
  "order_number" | "content"
> & {
  orgId: string;
  questionId: string | null;
  possibleAnswerId: string | null;
};

export async function postExample({
  orgId,
  order_number,
  content,
  questionId,
  possibleAnswerId,
}: PayloadPostExample) {
  const body = JSON.stringify({
    ...(questionId && { question_id: questionId }),
    ...(possibleAnswerId && { possible_answer_id: possibleAnswerId }),
    order_number: order_number,
    content: JSON.stringify(content),
  });

  return post(`/organisations/${orgId}/examples/`, body).then((r) => r.json());
}

type PayloadPatchChapter = {
  orgId: string;
  moduleId: string;
  chapterId: string;
  name: string;
  orderNumber: number;
};

export async function patchChapter({
  orgId,
  moduleId,
  chapterId,
  name,
  orderNumber,
}: PayloadPatchChapter) {
  const body = JSON.stringify({
    module_id: moduleId,
    name: name,
    order_number: orderNumber,
  });

  return patch(
    `/organisations/${orgId}/chapters/${chapterId}`,

    body
  ).then((r) => r.json());
}

type PayloadPatchQuestion = {
  orgId: string;
  question_id: string;
} & Partial<Question>;

export async function patchQuestion({
  orgId,
  question_id,
  order_number,
  content,
  conclusion,
  display_index,
  multiple,
  print,
  should_be_hidden,
  trigger,
  chapter_id,
  comment,
}: PayloadPatchQuestion) {
  const body = JSON.stringify({
    chapter_id,
    conclusion: conclusion,
    content: typeof content !== "string" ? JSON.stringify(content) : content,
    display_index,
    multiple,
    order_number,
    print: print,
    should_be_hidden,
    trigger: JSON.stringify(trigger),
    comment: comment,
  });

  return patch(
    `/organisations/${orgId}/questions/${question_id}`,

    body
  ).then((r) => r.json());
}

/*
  @function patchPossibleAnswer
  call patch for possible answer
  @param orgId {string} - organization id
  @param order_number {number} - order_number of chapter
  @param content {object} - content of possible answer
  @param single {boolean} - single variable
  @param questionId {string} - questionId 
  @param possibleAnswerId {string} - id for possible answer
*/
type PayloadPatchPossibleAnswer = {
  orgId: string;
  orderNumber: number;
  content: string | Record<string, any>;
  single: boolean;
  questionId: string;
  possibleAnswerId: string;
  comment?: string | null;
};

export async function patchPossibleAnswers({
  orgId,
  orderNumber,
  content,
  single,
  questionId,
  possibleAnswerId,
  comment,
}: PayloadPatchPossibleAnswer) {
  const body = JSON.stringify({
    question_id: questionId,
    content: typeof content === "string" ? content : JSON.stringify(content),
    order_number: orderNumber,
    single: single,
    comment: comment,
  });

  return patch(
    `/organisations/${orgId}/possible_answers/${possibleAnswerId}`,

    body
  ).then((r) => r.json());
}

/**
   @function patchGuidance
   patch guidance 
   @param orgId {string} - organization id
   @param order_number {number} - order_number of chapter
   @param content {object} - content of possible answer
   @param questionId {string} - questionId 
   @param possibleAnswerId {string} - id for possible answer
   @param guidanceId {string} - id for guidance
*/

type PayloadPatchGuidance = Partial<GuidanceOrExample> & {
  orgId: string;
  questionId: string | null;
};

export async function patchGuidance({
  orgId,
  order_number,
  content,
  questionId,
  possible_answer_id,
  guidance_id,
}: PayloadPatchGuidance) {
  const body = JSON.stringify({
    ...(questionId && { question_id: questionId }),
    ...(possible_answer_id && { possible_answer_id: possible_answer_id }),
    content: JSON.stringify(content),
    order_number: order_number,
  });

  return patch(
    `/organisations/${orgId}/guidances/${guidance_id}`,

    body
  ).then((r) => r.json());
}

/**
   @function patchExample
   patch guidance 
   @param orgId {string} - organization id
   @param order_number {number} - order_number of chapter
   @param content {object} - content of possible answer
   @param questionId {string} - questionId 
   @param possibleAnswerId {string} - id for possible answer
   @param exampleId {string} - id for guidance
*/

type PayloadPatchExample = Partial<GuidanceOrExample> & {
  orgId: string;
  questionId: string | null;
  possibleAnswerId: string | null;
};

export async function patchExample({
  orgId,
  order_number,
  content,
  questionId,
  possibleAnswerId,
  example_id,
}: PayloadPatchExample) {
  const body = JSON.stringify({
    ...(questionId && { question_id: questionId }),
    ...(possibleAnswerId && { possible_answer_id: possibleAnswerId }),
    content: JSON.stringify(content),
    order_number: order_number,
  });

  return patch(
    `/organisations/${orgId}/examples/${example_id}`,

    body
  ).then((r) => r.json());
}

type PayloadDeleteChapter = {
  orgId: string;
  chapterId: string;
};

export async function deleteChapter({
  orgId,
  chapterId,
}: PayloadDeleteChapter) {
  return deleteCall(`/organisations/${orgId}/chapters/${chapterId}`);
}

type PayloadDeleteQuestion = {
  orgId: string;
  questionId: string;
};

export async function deleteQuestion({
  orgId,
  questionId,
}: PayloadDeleteQuestion) {
  return deleteCall(`/organisations/${orgId}/questions/${questionId}`);
}

type PayloadDeletePossibleAnswer = {
  orgId: string;
  possibleAnswerId: string;
};

export async function deletePossibleAnswer({
  orgId,
  possibleAnswerId,
}: PayloadDeletePossibleAnswer) {
  return deleteCall(
    `/organisations/${orgId}/possible_answers/${possibleAnswerId}`
  );
}

type PayloadDeleteProgress = {
  progressId: string;
};

export async function deleteProgress({ progressId }: PayloadDeleteProgress) {
  return deleteCall(`/user/progress/${progressId}`);
}

/**
   @function deleteGuidance
   delete guidance
   @param orgId {string} - organization id
   @param guidanceId {string} - id for guidance
*/

type PayloadDeleteGuidance = {
  orgId: string;
  guidanceId: string;
};

export async function deleteGuidance({
  orgId,
  guidanceId,
}: PayloadDeleteGuidance) {
  return deleteCall(`/organisations/${orgId}/guidances/${guidanceId}`).then(
    (r) => r.json()
  );
}

/**
   @function deleteExamples
   delete example
   @param orgId {string} - organization id
   @param exampleId {string} - id for example
*/

type PayloadDeleteExample = {
  orgId: string;
  exampleId: string;
};

export async function deleteExample({
  orgId,
  exampleId,
}: PayloadDeleteExample) {
  return deleteCall(`/organisations/${orgId}/examples/${exampleId}`).then((r) =>
    r.json()
  );
}

type PayloadGetTerms = {
  orgId: string;
  isUpdated: boolean;
};

export async function getTerms({ orgId, isUpdated }: PayloadGetTerms) {
  return get(`/organisations/${orgId}/terms`, isUpdated)
    .then((r) => r.json())
    .then((r: { data: Term[] }) =>
      r.data.reduce((acc: Record<TermId, Term>, term) => {
        return { ...acc, [term.term_id]: term };
      }, {})
    );
}

/**
   @function getLinks
   get links per organization
   @param orgId {orgId} - organization Id
*/
export async function getLinks({
  orgId,
  isUpdated,
}: {
  orgId: string;
  isUpdated: boolean;
}) {
  return get(`/organisations/${orgId}/links`, isUpdated)
    .then((r) => r.json())
    .then((r: { data: Link[] }) =>
      r.data.reduce((acc: Record<LinkId, Link>, link) => {
        return { ...acc, [link.link_id]: link };
      }, {})
    );
}

/**
   @function getUserProgress
   get progress per user
   @param userId {userID} - user Id
*/
export async function getUserProgresses(userId: string, moduleId: string) {
  return get(`/user/progress?module_id=${moduleId}&user_id=${userId}`)
    .then((r) => r.json())
    .then((r: { data: UserProgress[] }) => r.data);
}

/**
   @function getProgress
   get progress per module
   @param moduleId {moduleID} - module Id
*/
export async function getProgress(moduleId: string) {
  return get(`/user/progress/${moduleId}`).then((r) => r.json());
}

/**
   @function getLinks
   get links per organization
   @param orgId {orgId} - organization Id
*/
export async function getReferences({
  orgId,
  isUpdated,
}: {
  orgId: string;
  isUpdated: boolean;
}) {
  return get(`/organisations/${orgId}/references`, isUpdated)
    .then((r) => r.json())
    .then((r: { data: Reference[] }) =>
      r.data.reduce((acc: Record<ReferenceId, Reference>, reference) => {
        return { ...acc, [reference.reference_id]: reference };
      }, {})
    );
}

type PayloadGetModule = {
  organizationId: string;
  moduleId: string;
  isUpdated: boolean;
};

export async function getModule({
  organizationId,
  moduleId,
  isUpdated,
}: PayloadGetModule) {
  return get(
    `/organisations/${organizationId}/modules/${moduleId}`,

    isUpdated
  )
    .then((r) => r.json())
    .then((r: { data: Module }) => r.data);
}

/**
   @function getUserProgress
   get user progress
   @param userId {string} - userId
   @param moduleId {string} - moduleId
*/

type PayloadGetUserProgress = {
  userId: string;
  moduleId: string;
};

export async function getUserProgress({
  userId,
  moduleId,
}: PayloadGetUserProgress) {
  return get(
    `/user/progress?module_id=${moduleId}&user_id=${userId}`,

    true
  )
    .then((r) => r.json())
    .then((r) => r.data);
}

export async function fetchClientAndTenantApi(
  email: string,
  identity_provider_type: string
) {
  const body = JSON.stringify({
    email,
    identity_provider_type,
  });

  return post("/user/identity_provider", body).then((r) => {
    if (r.status > 399) {
      return Promise.reject("error");
    }
    return r.json();
  });
}


type PayloadPatchUserManual = {
  orgId: string;
  moduleId: string;
  termId: string;
};

export async function patchUserManual({
  orgId,
  moduleId,
  termId
}: PayloadPatchUserManual) {
  const body = JSON.stringify({
    user_manual_term_id: termId
  });

  return patch(
    `/organisations/${orgId}/modules/${moduleId}`,
    body
  ).then((r) => r.json());
}